import { useSelector } from 'react-redux';
import { AuthStorage } from '@utils/auth';
import { RootState } from '@store';
import { Profile } from '@store/reducers/authReducer';
import { JwtPayload } from '@interfaces';

interface UseAuthReturn {
  isAuthenticated: boolean;
  profile: Profile;
  roles: string[];
  token: string | null;
  refreshToken: string | null;
  decodedToken: JwtPayload | null;
  sessionID: string | null;
  userId: string | null;
  hasRole: (role: string) => boolean;
  hasAnyRole: (requiredRoles: string[]) => boolean;
  hasAllRoles: (requiredRoles: string[]) => boolean;
}

/**
 * 통합 인증 상태 관리 훅
 *
 * Redux 상태와 localStorage를 결합하여 안전하게 인증 정보를 제공합니다.
 *
 * @returns {UseAuthReturn} 인증 상태 및 헬퍼 함수
 *
 * @example
 * const { isAuthenticated, profile, roles, hasRole } = useAuth();
 *
 * // 역할 확인
 * if (hasRole('ADMIN_SUPER')) {
 *   // 슈퍼 관리자 전용 기능
 * }
 *
 * // 프로필 표시
 * <div>{profile?.name}</div>
 */
const useAuth = (): UseAuthReturn => {
  // Redux 상태 가져오기
  const reduxAuth = useSelector((state: RootState) => state.auth);

  // localStorage에서 안전하게 데이터 가져오기
  const authData = AuthStorage.getAuthData();

  // 현재 역할 계산
  const getCurrentRoles = (): string[] => {
    return reduxAuth.roles?.length > 0 ? reduxAuth.roles : authData.roles;
  };

  return {
    // 인증 여부 (Redux 우선, localStorage로 폴백)
    isAuthenticated: reduxAuth.authenticated || AuthStorage.isAuthenticated(),

    // 프로필 정보 (Redux 우선, localStorage로 폴백)
    profile: reduxAuth.profile?.name
      ? reduxAuth.profile
      : authData.profile,

    // 역할 배열 (Redux 우선, localStorage로 폴백)
    roles: getCurrentRoles(),

    // 토큰 정보 (localStorage에서만 가져옴)
    token: authData.token,
    refreshToken: authData.refreshToken,

    // 디코딩된 토큰 정보
    decodedToken: authData.decodedToken,

    // 세션 정보
    sessionID: authData.sessionID,
    userId: authData.userId,

    // 헬퍼 함수들
    /**
     * 특정 역할 보유 여부 확인
     * @param role - 확인할 역할
     * @returns boolean
     */
    hasRole: (role: string): boolean => {
      return getCurrentRoles().includes(role);
    },

    /**
     * 여러 역할 중 하나라도 보유 여부 확인
     * @param requiredRoles - 확인할 역할 배열
     * @returns boolean
     */
    hasAnyRole: (requiredRoles: string[]): boolean => {
      return requiredRoles.some(role => getCurrentRoles().includes(role));
    },

    /**
     * 모든 역할 보유 여부 확인
     * @param requiredRoles - 확인할 역할 배열
     * @returns boolean
     */
    hasAllRoles: (requiredRoles: string[]): boolean => {
      return requiredRoles.every(role => getCurrentRoles().includes(role));
    },
  };
};

export default useAuth;
